import type { InputHTMLAttributes, MouseEvent, ReactNode, Ref } from "react";
import { useId, useRef } from "react";

import type { FormFieldPresentationProps, OmittedCommonFormFieldProps } from "@core/components/FormInputWrapper";
import { FORM_INPUT_STATUSES, FormInputWrapper } from "@core/components/FormInputWrapper";
import classnames from "classnames";

import styles from "./TextInput.module.scss";

export { FORM_INPUT_STATUSES as TEXT_INPUT_STATUSES };

export type TextInputProps = FormFieldPresentationProps & {
  elemBefore?: ReactNode;
  elemAfter?: ReactNode;
  ref?: Ref<HTMLInputElement>;
  type?: "text" | "number" | "email" | "password" | "number" | "search" | "tel" | "url";
} & Omit<InputHTMLAttributes<HTMLInputElement>, OmittedCommonFormFieldProps | "type">;

export const TextInput = ({
  labelFields,
  labelId,
  value,
  disabled = false,
  readOnly = false,
  status = "default",
  helperText,
  maxLength,
  elemBefore,
  elemAfter,
  name,
  required,
  id: externalId,
  ref,
  type,
  "aria-describedby": externalDescribedBy,
  showCharacterCount = false,
  ...rest
}: TextInputProps) => {
  const generatedId = useId();
  const inputId = externalId ?? generatedId;
  const helperTextId = helperText ? `${inputId}-supporting` : undefined;
  const describedBy = [helperTextId, externalDescribedBy].filter(Boolean).join(" ") || undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  const setInputRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;

    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const focusInputOnWrapperClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled || event.target !== event.currentTarget) return;

    inputRef.current?.focus();
  };

  return (
    <FormInputWrapper
      labelFields={labelFields}
      labelId={labelId}
      disabled={disabled}
      required={required}
      status={status}
      helperText={helperText}
      helperTextId={helperTextId}
      maxLength={maxLength}
      value={value}
      inputId={inputId}
      showCharacterCount={showCharacterCount}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- the wrapper only forwards clicks to the input, which stays keyboard accessible */}
      <div
        className={classnames(styles.inputWrapper, {
          [styles.invalid]: status === "error",
          [styles.success]: status === "success",
          [styles.readOnly]: readOnly,
        })}
        onClick={focusInputOnWrapperClick}
      >
        {elemBefore && <span className={styles.elemSlot}>{elemBefore}</span>}
        <input
          {...rest}
          id={inputId}
          name={name}
          className={styles.input}
          value={value}
          maxLength={maxLength}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          type={type}
          aria-invalid={status === "error" || undefined}
          aria-describedby={describedBy}
          ref={setInputRef}
        />
        {elemAfter && <span className={styles.elemSlot}>{elemAfter}</span>}
      </div>
    </FormInputWrapper>
  );
};
